import styled from "@emotion/styled";
import { CalendarTodayOutlined } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Pencil } from "@phosphor-icons/react";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import {
  useChartOfAccountsPaginated,
  useFinanceSettingsStore
} from "features/finances";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FilterLedgerModal } from "../Modals/FilterLedgerModal";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const ChartOfAccountTable = ({ onEdit, onboarding }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openFilterLedgerModal, setOpenFilterLedgerModal] = useState(false);
  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault("")
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault("")
  );

  const openCreateChartOfAccountModal = useFinanceSettingsStore(
    state => state.openCreateChartOfAccountModal
  );
  const openDefaultChartOfAccountsModal = useFinanceSettingsStore(
    state => state.openDefaultChartOfAccountsModal
  );
  const openUpdateChartOfAccountModal = useFinanceSettingsStore(
    state => state.openUpdateChartOfAccountModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const chartOfAccountsQuery = useChartOfAccountsPaginated({
    churchId: selectedChurch?.id,
    page,
    pageSize,
    startDate,
    endDate
  });

  const columns = [
    {
      field: "account_number",
      headerName: "Account Number",
      flex: 1
    },
    {
      field: "account_name",
      headerName: "Account Name",
      flex: 1,
      renderCell: ({ row }) => {
        return <>{row.account_name}</>;
      }
    },
    {
      field: "account_type",
      headerName: "Account Type",
      flex: 1
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1
    },
    {
      field: "beginning_balance",
      headerName: "Beginning Balance",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "ending_balance",
      headerName: "Ending Balance",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "account_status",
      headerName: "Status",
      flex: 1
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      hide: onboarding,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row">
            <IconButton
              onClick={e => {
                e.stopPropagation();
                onEdit(row);
                openUpdateChartOfAccountModal();
              }}
            >
              <Pencil />
            </IconButton>
          </Stack>
        );
      }
    }
  ];

  return (
    <Container>
      {!onboarding && (
        <Stack alignItems="flex-start" spacing={4}>
          <Header>
            <Title>Chart Of Account List</Title>
            <PrimaryActions>
              <Button onClick={openCreateChartOfAccountModal}>
                New Account
              </Button>
              <Button
                flavor="outlined"
                onClick={openDefaultChartOfAccountsModal}
              >
                Generate Default Accounts
              </Button>
              <Button
                flavor="outlined"
                endIcon={<CalendarTodayOutlined />}
                onClick={() => setOpenFilterLedgerModal(true)}
              >
                Filter by date
              </Button>
            </PrimaryActions>
          </Header>
        </Stack>
      )}
      <DataGrid
        autoHeight
        rows={chartOfAccountsQuery.data?.result?.data || []}
        rowCount={chartOfAccountsQuery.data?.result?.total || 0}
        loading={chartOfAccountsQuery?.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        onRowClick={({ row }) => {
          if (onboarding) return;

          navigate(
            `/finances/temp/chart-of-accounts/${row.id}?accountName=${row.account_name}&accountNumber=${row.account_number}&accountType=${row.account_type}`
          );
        }}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
      />

      <FilterLedgerModal
        open={openFilterLedgerModal}
        onClose={() => setOpenFilterLedgerModal(false)}
        onApply={({ startDate, endDate }) => {
          setStartDate(startDate);
          setEndDate(endDate);
          setOpenFilterLedgerModal(false);
        }}
      />
    </Container>
  );
};
